'use client'

import { useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useSession } from 'next-auth/react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { apiClient, apiStreamPost, type StreamStep, type StreamToolCall } from '@/lib/api-client'
import { useAI } from '@/context/AIContext'
import { buildPageContext } from '@/lib/ai-context-builder'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { CapabilityBubbles } from './CapabilityBubbles'
import { useCaseStore } from '@/components/hr-case-management/store/caseStore'
import type { HRCase, CaseType, CaseTier, CaseStatus, ResolutionPathway } from '@/components/hr-case-management/types/case.types'

// ── Case factory — builds a live HRCase from an AI query ──────────────────

function buildCaseFromQuery(userMsg: string, aiResponse: string): HRCase | null {
  // Only create a case if the AI response contains a case classification
  if (!aiResponse.includes('Case Classification:')) return null

  const id = `ai-${Date.now()}`
  const ref = `CASE-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 900) + 100).padStart(4,'0')}`
  const now = new Date().toISOString()
  const sla = new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString()

  // Extract tier
  const tierMatch = aiResponse.match(/Case Classification:\s*(L[12])/i)
  const tier: CaseTier = tierMatch?.[1]?.toUpperCase() === 'L1' ? 'L1' : 'L2'

  // Extract resolution pathway
  let resolutionPathway: ResolutionPathway = 'autonomous'
  if (/Workbench Escalation/i.test(aiResponse)) resolutionPathway = 'workbench_escalation'
  else if (/Approval/i.test(aiResponse)) resolutionPathway = 'approval_required'

  // Map status from pathway
  const statusMap: Record<ResolutionPathway, CaseStatus> = {
    autonomous: 'investigating',
    approval_required: 'awaiting_approval',
    workbench_escalation: 'in_workbench',
  }
  const status = statusMap[resolutionPathway]

  // Guess case type from keywords
  const lowerMsg = userMsg.toLowerCase()
  let type: CaseType = 'payroll_query'
  if (/salary|pay|payroll|paid|payment/.test(lowerMsg))    type = 'payroll_query'
  else if (/pto|leave|vacation|time off/.test(lowerMsg))   type = 'pto_leave_balance'
  else if (/benefit|insurance|health/.test(lowerMsg))      type = 'benefits_policy_question'
  else if (/address|data change|update/.test(lowerMsg))    type = 'address_data_change'
  else if (/employment|verification/.test(lowerMsg))       type = 'employment_verification'
  else if (/accommodation|disability/.test(lowerMsg))      type = 'accommodation_disability'
  else if (/exception|appeal/.test(lowerMsg))              type = 'exception_appeal'
  else if (/relation|conflict|harassment|sensitive/.test(lowerMsg)) type = 'sensitive_employee_relations'
  else if (/entitlement|eligibility/.test(lowerMsg))       type = 'entitlement_eligibility'

  // Extract employee name — look for "named X" or "called X" or "for X"
  const nameMatch = userMsg.match(/(?:named|called|for|employee)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i)
  const employeeName = nameMatch?.[1] ?? 'Employee (via AI Manager)'

  const agentNameLine = aiResponse.match(/Assigned Agent:\s*(.+)/i)
  const agentName = agentNameLine?.[1]?.trim() ?? 'Triage Agent'

  return {
    id,
    caseRef: ref,
    tier,
    type,
    status,
    channel: 'teams',
    submittedAt: now,
    slaDeadline: sla,
    slaBreached: false,
    employee: {
      id: `emp-ai-${Date.now()}`,
      name: employeeName,
      email: `${employeeName.toLowerCase().replace(/\s+/g, '.')}@company.com`,
      department: 'Unknown',
      location: 'Remote',
      employeeId: `EMP-AI-${Math.floor(Math.random() * 9000) + 1000}`,
      manager: 'Department Manager',
      startDate: '2022-01-01',
    },
    subject: userMsg.length > 80 ? userMsg.slice(0, 80) + '...' : userMsg,
    description: userMsg,
    resolutionPathway,
    policyTriggers: [],
    agentActions: [{
      agentName,
      action: 'Case received via AI Manager. Initial triage completed.',
      timestamp: now,
      systemTouched: 'AI Manager',
      outcome: 'success',
      detail: 'Routed from AI Manager query.',
    }],
    workbenchNote: resolutionPathway === 'workbench_escalation'
      ? `Case escalated via AI Manager query. Original request: "${userMsg}"`
      : undefined,
    relatedSystems: ['ServiceNow', 'Workday'],
    chatHistory: [
      {
        id: `${id}-m1`,
        sender: 'employee',
        senderName: employeeName,
        message: userMsg,
        timestamp: now,
      },
      {
        id: `${id}-m2`,
        sender: 'ai_agent',
        senderName: agentName,
        message: aiResponse.split('\n')[0] ?? 'Your case has been received and is being processed.',
        timestamp: new Date(Date.now() + 2000).toISOString(),
      },
    ],
  }
}

/**
 * Fold one step frame into the steps already on screen.
 *
 * Added 2026-09-22. Every step arrives twice, once `running` and once `done`,
 * with the same label in both, so the label is the key: a step already on screen
 * is updated in place and keeps its position, and a label not seen before is
 * appended below the ones above it. Nothing is ever removed, because the panel
 * is a record of what was read and not a spinner.
 *
 * A `done` frame is allowed to omit the detail the `running` frame carried, so
 * the previous detail is kept rather than blanked when the new one is missing.
 */
function mergeStep(existing: StreamStep[] | undefined, step: StreamStep): StreamStep[] {
  const steps = existing ? [...existing] : []
  const at = steps.findIndex((s) => s.label === step.label)
  if (at === -1) {
    steps.push(step)
    return steps
  }
  steps[at] = { ...steps[at], ...step, detail: step.detail ?? steps[at].detail }
  return steps
}

// ============================================================================
// Animation Variants
// ============================================================================

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300,
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.15,
    }
  },
}

// ============================================================================
// AI Manager Component
// ============================================================================

export function AIManager() {
  const { data: session } = useSession()
  const {
    isManagerOpen,
    closeManager,
    chatHistory,
    addMessage,
    updateLastMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    currentPageContext,
    currentPageSection,
  } = useAI()

  const caseStore = useCaseStore()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [chatHistory, isTyping])

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isManagerOpen) {
        closeManager()
      }
    }
    
    if (isManagerOpen) {
      document.addEventListener('keydown', handleKeyDown)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isManagerOpen, closeManager])

  // Focus trap - focus the modal when it opens
  useEffect(() => {
    if (isManagerOpen && modalRef.current) {
      modalRef.current.focus()
    }
  }, [isManagerOpen])

  // Handle sending a message
  const handleSendMessage = useCallback(async (content: string) => {
    // Add user message
    addMessage({ role: 'user', content })

    // Add loading message
    addMessage({ role: 'assistant', content: '', isLoading: true })
    setIsTyping(true)

    // The same body either way, so the streamed answer and the whole-answer
    // fallback are answers to the same question with the same context.
    const body = {
      message: content,
      history: chatHistory.filter(m => !m.isLoading).map(m => ({
        role: m.role,
        content: m.content,
      })),
      context: {
        page: currentPageContext,
        // The sub-view the user is on. Pages publish it through
        // `useAIPageSection`; without it the backend can only see the
        // pathname, which on the Operations Hub is the same string for five
        // completely different screens.
        section: currentPageSection,
        selected_data: buildPageContext(currentPageContext, currentPageSection),
      },
    }

    // Injecting a live case is what the panel has always done once an answer
    // lands, and it must keep happening exactly once whichever path produced
    // the answer.
    const injectCase = (responseText: string) => {
      if (!caseStore) return
      const newCase = buildCaseFromQuery(content, responseText)
      if (newCase) caseStore.addCase(newCase)
    }

    try {
      // Streamed first, added 2026-09-22. The deterministic answers send named
      // steps ahead of the text, so the room watches the systems being read
      // instead of watching three dots, and the answer then arrives at reading
      // speed rather than all at once after a silent wait.
      try {
        let streamed = ''
        // The tools the turn ran, kept here and written onto the message at the
        // end. The deterministic answers call none; a model answer that
        // generates a report calls one and the page draws its download button
        // from this, which the streamed path used to lose.
        let streamedTools: StreamToolCall[] = []
        const full = await apiStreamPost(
          '/api/ai/chat/stream',
          body,
          (fragment) => {
            streamed += fragment
            const soFar = streamed
            updateLastMessage(() => ({ content: soFar, isLoading: false, isStreaming: true }))
          },
          (step) => {
            updateLastMessage((previous) => ({ steps: mergeStep(previous.steps, step) }))
          },
          (tools) => {
            streamedTools = streamedTools.concat(tools)
          }
        )

        const responseText = (full || streamed).trim()
        if (responseText) {
          // isStreaming goes false here and only here, which is what releases a
          // held-back trailing block to the renderer.
          updateLastMessage(() => ({
            content: responseText,
            isLoading: false,
            isStreaming: false,
            toolCalls: streamedTools.length > 0 ? streamedTools : undefined,
          }))
          injectCase(responseText)
          return
        }
      } catch {
        // Fall through to the whole-answer endpoint. A proxy that buffers, an
        // old browser or one bad frame must cost the streaming and never the
        // answer.
      }

      // Put the bubble back the way the loading state left it, so any partial
      // text already streamed is discarded and `addMessage` can retire this
      // message as the loading placeholder it was.
      updateLastMessage(() => ({
        content: '',
        steps: undefined,
        isLoading: true,
        isStreaming: false,
      }))

      // Call backend API using apiClient
      interface ToolCallResponse {
        id: string
        name: string
        args: Record<string, unknown>
        result?: unknown
      }
      const data = await apiClient.post<{ response: string; tool_calls?: ToolCallResponse[] }>('/api/ai/chat', body)

      const responseText = data.response || 'I apologize, but I encountered an issue processing your request.'

      // Remove loading message and add real response
      addMessage({
        role: 'assistant',
        content: responseText,
        toolCalls: data.tool_calls,
      })

      // Inject a live case into the queue if the AI classified this as a case
      injectCase(responseText)
    } catch {
      addMessage({
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.',
      })
    } finally {
      setIsTyping(false)
    }
  }, [addMessage, caseStore, chatHistory, currentPageContext, currentPageSection, setIsTyping, updateLastMessage])

  // Handle quick action click
  const handleQuickAction = (action: string) => {
    handleSendMessage(action)
  }

  // Get page name from context
  const getPageName = () => {
    if (currentPageContext === '/') return 'Dashboard'
    const segments = currentPageContext.split('/').filter(Boolean)
    return segments.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' > ')
  }

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeManager()
    }
  }

  // Only render in browser (for portal)
  if (typeof window === 'undefined') return null

  return createPortal(
    <AnimatePresence mode="wait">
      {isManagerOpen && (
        <>
          {/* Backdrop — aria-hidden so screen readers skip it */}
          <motion.div
            key="ai-manager-backdrop"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2 }}
            onClick={handleBackdropClick}
            className="fixed inset-0 z-modal-backdrop"
            style={{
              backgroundColor: 'rgba(26, 35, 64, 0.5)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            aria-hidden="true"
          />

          {/* Modal — sibling of backdrop, not child, so aria-hidden doesn't inherit */}
          <div className="fixed inset-0 z-modal flex items-center justify-center p-4 sm:p-8 md:p-12 pointer-events-none">
            <motion.div
              ref={modalRef}
              key="ai-manager-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ai-manager-title"
              aria-describedby="ai-manager-description"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
              style={{ pointerEvents: 'auto' }}
              className={cn(
                // Z-index for proper stacking
                'z-modal',
                // Size and positioning
                'relative w-full max-w-2xl',
                'h-[80vh] max-h-[700px] min-h-[450px]',
                // Solid white background for better readability
                'bg-white',
                // Border and shadow
                'border border-gray-200',
                'rounded-2xl',
                'shadow-2xl',
                // Layout
                'flex flex-col',
                'overflow-hidden'
              )}
            >
              {/* Decorative gradient border */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-cornflower/20 via-transparent to-brand-purple/20 pointer-events-none" />
              
              {/* Header */}
              <div className="relative border-b border-border/30 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* AI Icon */}
                    <div className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-xl',
                      'bg-gradient-to-br from-brand-navy via-brand-purple to-brand-cornflower',
                      'shadow-lg shadow-brand-purple/25',
                      'ring-2 ring-white/50'
                    )}>
                      <Icons.sparkles className="h-6 w-6 text-white" strokeWidth={1.5} />
                    </div>
                    
                    <div>
                      <div className='flex items-center gap-2'>
                        <h2
                          id="ai-manager-title"
                          className="text-xl font-semibold text-brand-navy"
                        >
                          AI Manager
                        </h2>
                        <span className='flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[12px] font-semibold text-emerald-700'>
                          <span className='h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse' />
                          Live
                        </span>
                      </div>
                      <p
                        id="ai-manager-description"
                        className="text-xs text-muted-foreground"
                      >
                        Corporate Services Command Center · L1/L2 · 9 coworkers online
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {chatHistory.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearHistory}
                        className="text-muted-foreground hover:text-foreground gap-1.5"
                        title="Clear conversation"
                      >
                        <Icons.trash className="h-4 w-4" strokeWidth={1.5} />
                        <span className="hidden sm:inline">Clear</span>
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={closeManager}
                      className="text-muted-foreground hover:text-foreground hover:bg-red-50 hover:text-red-600"
                      title="Close (Esc)"
                    >
                      <Icons.close className="h-5 w-5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>

                {/* Page Context Badge */}
                <div className="mt-4 flex items-center gap-3">
                  <div className={cn(
                    'inline-flex items-center gap-2 rounded-full px-3 py-1.5',
                    'bg-gradient-to-r from-brand-cornflower/10 to-brand-purple/10',
                    'text-brand-navy border border-brand-cornflower/20',
                    'text-sm font-medium'
                  )}>
                    <Icons.globe className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
                    <span>Viewing: {getPageName()}</span>
                  </div>
                  
                  <kbd className={cn(
                    'hidden items-center rounded-md px-2 py-1 sm:inline-flex',
                    'text-xs font-medium',
                    'border border-border/50 bg-muted/50 text-muted-foreground'
                  )}>
                    <Icons.command className="h-3 w-3 mr-1" />
                    J to toggle
                  </kbd>
                </div>
              </div>

              {/* Messages Area */}
              <div className="relative flex-1 overflow-y-auto px-6 py-5">
                {chatHistory.length === 0 ? (
                  /* Empty State */
                  <div className="flex h-full flex-col items-center justify-center text-center py-8">
                    <div className={cn(
                      'mb-6 flex h-20 w-20 items-center justify-center rounded-2xl',
                      'bg-gradient-to-br from-brand-cornflower/20 to-brand-purple/20',
                      'ring-1 ring-brand-cornflower/30'
                    )}>
                      <Icons.messageSquare className="h-10 w-10 text-brand-cornflower" strokeWidth={1.5} />
                    </div>
                    
                    <h3 className="font-display text-xl font-semibold text-brand-navy">
                      AI Manager
                    </h3>
                    <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                      Ask any HR query — L1/L2 classification, case status, workforce analytics, or policy creation.
                    </p>

                    {/* Quick Actions - Capability Bubbles */}
                    <div className="mt-8 w-full max-w-lg">
                      <CapabilityBubbles onSelect={handleQuickAction} />
                    </div>
                  </div>
                ) : (
                  /* Chat Messages */
                  <div className="space-y-4">
                    {chatHistory.map((message) => (
                      <ChatMessage
                        key={message.id}
                        message={message}
                        userName={session?.user?.name || undefined}
                        userImage={session?.user?.image}
                      />
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="relative border-t border-border/30">
                <ChatInput
                  onSend={handleSendMessage}
                  disabled={isTyping}
                  placeholder={isTyping ? 'AI Manager is processing...' : 'Ask anything — HR queries, L1/L2 triage, case status, analytics...'}
                />
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
